import {
	type LoaderFunctionArgs,
	type MetaFunction,
	Link,
	useSearchParams,
	useLoaderData,
} from 'react-router'
import { useTranslation } from 'react-i18next'
import { LoadMoreButton } from '~/components/button/load-more-button'
import Container from '~/components/container/container'
import EventList from '~/components/event-list/event-list'
import { Icon } from '~/components/icon/icon'
import { Spacer } from '~/components/spacer/spacer'
import Text from '~/components/text/text'
import Toggle from '~/components/toggle/Toggle'
import { getSingleUser } from '~/queries/getSingleUser.server'
import { getIndexEvents } from '~/queries/indexEvents/getIndexEvents.server'
import { getServerLang } from '~/utils/i18n.utils'
import { getImgSrc } from '~/utils/image'
import { invariantResponse } from '~/utils/misc'
import { SetMeta } from '~/utils/seo/seo'
import { useIsAdmin } from '~/utils/user'
import i18next from '~/configs/i18next.server'
import { getUserLocation } from '~/utils/location/location.server'

export async function loader({ params, request }: LoaderFunctionArgs) {
	invariantResponse(params.userId, 'Missing ID', { status: 400 })
	const user = await getSingleUser(params.userId)
	invariantResponse(user, 'user', { status: 404 })
	const userLocation = await getUserLocation(request, params)
	const lng = getServerLang(request)
	const url = new URL(request.url)
	const showOld = url.searchParams.get('showOld') ?? undefined
	const page = parseInt(url.searchParams.get('page') ?? '1')
	const pageSize = 20
	const events = await getIndexEvents({
		ownerId: params.userId,
		showOldEvents: Boolean(showOld),
		take: pageSize * page,
		lng: lng,
	})
	const t = await i18next.getFixedT(lng.lngShort, 'common')

	return {
		user,
		events,
		page,
		pageSize,
		countryCode: userLocation.countryCode,
		title: t('error_404'),
		description: t('error_could_not_find_user'),
	}
}

export default function UserIdIndex() {
	const { user, events, page, pageSize } = useLoaderData<typeof loader>()
	const { t } = useTranslation()
	const [searchParams, setSearchParams] = useSearchParams()
	const showOld = searchParams.get('showOld')
	const isAdmin = useIsAdmin()
	const handleToggle = () => {
		const newSearchParams = new URLSearchParams(searchParams.toString())
		if (showOld) {
			newSearchParams.delete('showOld')
		} else {
			newSearchParams.set('showOld', 'true')
		}
		setSearchParams(newSearchParams)
	}

	return (
		<Container>
			<div className="w-full">
				<div className="flex flex-row items-center">
					<img
						src={getImgSrc(user.imageId, 'user')}
						alt={user.name}
						className="mr-2 h-16 w-16 rounded-full object-cover md:mr-6"
					/>
					<div className="truncate overflow-hidden">
						<Text as="h1" className="max-md:text-body-lg truncate">
							{user.name}
						</Text>
						{user.email && (
							<Text as="h3" className="truncate">
								<Link to={`mailto:${user.email}`} className="text-secondary">
									{user.email}
								</Link>
							</Text>
						)}
					</div>
				</div>
				<div className="mt-3">
					{user.phonenumber && (
						<Text className="mt-2">
							<Icon name="phone" className="mr-2" size="sm" />
							<Link className="underline" to={`tel:${user.phonenumber}`}>
								{user.phonenumber}
							</Link>
						</Text>
					)}
					{user.website && (
						<Text className="mt-2">
							<Icon name="globe" className="mr-2" size="sm" />
							<Link
								className="underline"
								to={`${user.website}`}
								target="_blank"
								rel="noreferrer"
							>
								{t('misc_website')}
							</Link>
						</Text>
					)}
					{user.address && (
						<Text className="mt-2">
							<Icon name="position-marker" className="mr-2" size="sm" />
							<a
								href={`https://maps.google.com/?q=${user.address}`}
								target="_blank"
								rel="noreferrer"
								className="underline"
							>
								{t('location_address')}
							</a>
						</Text>
					)}
				</div>
				{user.description && <Text className="mt-3">{user.description}</Text>}
				<Spacer size="4xs" />
				<hr />
				{isAdmin && (
					<>
						<Spacer size="4xs" />
						<div>
							<Toggle
								checked={Boolean(showOld)}
								onChange={handleToggle}
								label={t('events_show_previous_events')}
							/>
						</div>
					</>
				)}
				<Spacer size="4xs" />

				<EventList events={events} aspectRatio="video" size="md" showFavorite />

				<LoadMoreButton
					page={page}
					pageSize={pageSize}
					count={events.length}
					text={t('action_show_more_events')}
					loadingText={t('events_loading_events')}
				/>
			</div>
		</Container>
	)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const meta: MetaFunction<typeof loader> = ({ data }: { data: any }) => {
	if (!data)
		return SetMeta({
			title: data.title,
			description: data.description,
			country: data?.countryCode,
		})
	const displayName = data?.user.name ?? ''
	return SetMeta({
		title: `${displayName}`,
		description: data?.user.description ?? data.description,
		image: getImgSrc(data?.user.imageId, 'user'),
		country: data?.countryCode,
	})
}
